// Phase 10B — Campaigns page restyled to AutoMind 2050.
import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Download, RefreshCw, Play, Pause, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { campaignsApi } from '../../api';
import { PageHeader } from '../../components/ui/page-header';
import { TabBar } from '../../components/ui/tab-bar';
import { DataCard } from '../../components/ui/data-card';
import { MetricCard } from '../../components/ui/metric-card';
import { SectionGrid } from '../../components/ui/section-grid';
import { StatusPill } from '../../components/ui/status-pill';
import { EmptyState } from '../../components/ui/empty-state';
import CreateCampaignDialog from './CreateCampaignDialog';
import EditCampaignDialog from './EditCampaignDialog';

const STATUS_TONE = {
  active:    { tone: 'success', label: 'Active' },
  paused:    { tone: 'warning', label: 'Paused' },
  completed: { tone: 'neutral', label: 'Completed' },
  scheduled: { tone: 'info',    label: 'Scheduled' },
  draft:     { tone: 'neutral', label: 'Draft' },
};

const STATUS_TABS = [
  { id: 'active',    label: 'Active' },
  { id: 'paused',    label: 'Paused' },
  { id: 'completed', label: 'Completed' },
  { id: 'draft',     label: 'Drafts' },
];

const Campaigns = () => {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [statusTab, setStatusTab] = useState('active');

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await campaignsApi.list();
      setCampaigns(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching campaigns:', err);
      setError(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchCampaigns();
  };

  const handlePauseResume = async (campaignId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'paused' : 'active';
      await campaignsApi.update(campaignId, { status: newStatus });
      toast.success(`Campaign ${newStatus === 'active' ? 'started' : 'paused'} successfully`);
      await fetchCampaigns();
    } catch (err) {
      console.error('Error updating campaign:', err);
      toast.error(`Failed to update campaign: ${err.message}`);
    }
  };

  const handleDeleteCampaign = async (campaignId, campaignName) => {
    if (!window.confirm(`Are you sure you want to delete "${campaignName}"? This cannot be undone.`)) return;

    try {
      await campaignsApi.remove(campaignId);
      toast.success(`Campaign "${campaignName}" has been deleted`);
      await fetchCampaigns();
    } catch (err) {
      console.error('Error deleting campaign:', err);
      toast.error(`Failed to delete campaign: ${err.message}`);
    }
  };

  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setShowEditDialog(true);
  };

  const downloadSampleCSV = () => {
    const csvContent = `First Name,Last Name,Phone Number,Email,Vehicle Interest,Trade-In Vehicle,Preferred Contact Time,Notes
John,Smith,+12045551234,john.smith@example.com,SUV,2020 Honda CR-V,Afternoon,Interested in new models
Jane,Doe,+12045555678,jane.doe@example.com,Truck,2018 Ford F-150,Morning,Looking for financing options
Mike,Johnson,+12045559012,mike.j@example.com,Sedan,,Evening,Trade-in inquiry
Sarah,Williams,+12045553456,sarah.w@example.com,Electric Vehicle,2019 Nissan Leaf,Anytime,Test drive requested`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'leads_sample_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Derive numeric counters from each campaign, supporting either
  // legacy fields (leads/completed/booked) or richer ones if present.
  const enriched = useMemo(() => campaigns.map((c) => {
    const totalLeads       = c.total_leads       ?? c.leads        ?? 0;
    const callsAttempted   = c.calls_attempted   ?? c.completed    ?? 0;
    const callsConnected   = c.calls_connected   ?? c.connected    ?? 0;
    const appointmentsBooked = c.appointments_booked ?? c.booked   ?? 0;
    return { ...c, totalLeads, callsAttempted, callsConnected, appointmentsBooked };
  }), [campaigns]);

  const kpis = useMemo(() => ({
    activeCampaigns: enriched.filter((c) => c.status === 'active').length,
    totalLeads: enriched.reduce((acc, c) => acc + (c.totalLeads || 0), 0),
    callsConnected: enriched.reduce((acc, c) => acc + (c.callsConnected || 0), 0),
    appointmentsBooked: enriched.reduce((acc, c) => acc + (c.appointmentsBooked || 0), 0),
  }), [enriched]);

  const filteredCampaigns = useMemo(() => {
    return enriched.filter((c) => {
      const status = c.status || 'draft';
      if (statusTab === 'draft') return status === 'draft';
      return status === statusTab;
    });
  }, [enriched, statusTab]);

  const headerActions = (
    <>
      <button
        type="button"
        onClick={handleRefresh}
        disabled={refreshing}
        className="btn-2050 btn-ghost"
        data-testid="refresh-campaigns-btn"
      >
        <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
        Refresh
      </button>
      <button
        type="button"
        onClick={downloadSampleCSV}
        className="btn-2050 btn-secondary"
        data-testid="download-sample-csv-button"
      >
        <Download className="w-3.5 h-3.5" />
        CSV template
      </button>
      <button
        type="button"
        onClick={() => setShowCreateDialog(true)}
        className="btn-2050 btn-primary"
        data-testid="create-campaign-button"
      >
        <Plus className="w-3.5 h-3.5" />
        New campaign
      </button>
    </>
  );

  const renderRows = () => {
    if (error) {
      return (
        <EmptyState
          title="Couldn't load campaigns"
          description="Unable to load right now."
        />
      );
    }
    if (filteredCampaigns.length === 0) {
      return (
        <EmptyState
          title="No campaigns"
          description={
            statusTab === 'active'
              ? 'No active campaigns. Create one to start dialing.'
              : `No ${statusTab} campaigns.`
          }
        >
          <button
            type="button"
            onClick={() => setShowCreateDialog(true)}
            className="btn-2050 btn-primary"
          >
            <Plus className="w-3.5 h-3.5" />
            New campaign
          </button>
        </EmptyState>
      );
    }
    return (
      <div className="overflow-x-auto -mx-6" data-testid="campaign-list">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-[0.16em] text-ink-500 bg-ink-50/60 dark:bg-ink-900/40">
              <th className="text-left px-6 py-3 font-medium">Name</th>
              <th className="text-left px-6 py-3 font-medium">Status</th>
              <th className="text-right px-6 py-3 font-medium">Total leads</th>
              <th className="text-right px-6 py-3 font-medium">Attempted</th>
              <th className="text-right px-6 py-3 font-medium">Connected</th>
              <th className="text-right px-6 py-3 font-medium">Booked</th>
              <th className="text-right px-6 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-200/60 dark:divide-ink-800/60">
            {filteredCampaigns.map((c) => {
              const statusInfo = STATUS_TONE[c.status] || STATUS_TONE.draft;
              return (
                <tr
                  key={c.id}
                  className="row-2050 hover:bg-ink-50/60 dark:hover:bg-ink-900/40 align-top"
                  data-testid={`campaign-row-${c.id}`}
                >
                  <td className="px-6 py-3">
                    <div className="font-medium text-ink-900 dark:text-ink-50">{c.name}</div>
                    {c.type && (
                      <div className="text-xs text-ink-500 mt-0.5">{c.type}</div>
                    )}
                  </td>
                  <td className="px-6 py-3">
                    <StatusPill tone={statusInfo.tone} label={statusInfo.label} dot />
                  </td>
                  <td className="px-6 py-3 nums text-right text-ink-900 dark:text-ink-50">
                    {Number(c.totalLeads).toLocaleString()}
                  </td>
                  <td className="px-6 py-3 nums text-right text-ink-700 dark:text-ink-200">
                    {Number(c.callsAttempted).toLocaleString()}
                  </td>
                  <td className="px-6 py-3 nums text-right text-ink-700 dark:text-ink-200">
                    {Number(c.callsConnected).toLocaleString()}
                  </td>
                  <td className="px-6 py-3 nums text-right text-ink-900 dark:text-ink-50 font-medium">
                    {Number(c.appointmentsBooked).toLocaleString()}
                  </td>
                  <td className="px-6 py-3 text-right whitespace-nowrap">
                    <div className="inline-flex gap-1">
                      {c.status === 'active' ? (
                        <button
                          type="button"
                          className="btn-2050 btn-secondary"
                          onClick={() => handlePauseResume(c.id, c.status)}
                        >
                          <Pause className="w-3 h-3" />
                          Pause
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn-2050 btn-primary"
                          onClick={() => handlePauseResume(c.id, c.status)}
                        >
                          <Play className="w-3 h-3" />
                          Start
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn-2050 btn-ghost"
                        onClick={() => handleEditCampaign(c)}
                        data-testid={`edit-campaign-${c.id}`}
                      >
                        <Edit className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        className="btn-2050 btn-ghost"
                        onClick={() => handleDeleteCampaign(c.id, c.name)}
                        data-testid={`delete-campaign-${c.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Outbound"
        title="Campaigns"
        description="Calling campaigns and their progress."
        actions={headerActions}
      />

      <SectionGrid cols={4}>
        <MetricCard
          label="Active campaigns"
          value={kpis.activeCampaigns}
          helperText="Currently running"
        />
        <MetricCard
          label="Total leads"
          value={kpis.totalLeads}
          helperText="Across all campaigns"
        />
        <MetricCard
          label="Calls connected"
          value={kpis.callsConnected}
          helperText="Lifetime"
        />
        <MetricCard
          label="Appointments booked"
          value={kpis.appointmentsBooked}
          helperText="Lifetime"
        />
      </SectionGrid>

      <TabBar value={statusTab} onChange={setStatusTab} items={STATUS_TABS} />

      <DataCard
        title="Campaigns"
        description={`${filteredCampaigns.length} of ${campaigns.length} shown`}
        bodyClassName="p-0"
        loading={loading && campaigns.length === 0}
      >
        {renderRows()}
      </DataCard>

      <CreateCampaignDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onCreated={fetchCampaigns}
      />

      <EditCampaignDialog
        open={showEditDialog}
        campaign={editingCampaign}
        onOpenChange={(open) => {
          setShowEditDialog(open);
          if (!open) setEditingCampaign(null);
        }}
        onSaved={fetchCampaigns}
      />
    </div>
  );
};

export default Campaigns;
